/**
 * Server-side auth calls, backed by vecros-api (no Amplify).
 *
 * Ported from the accounts app (`NewBackendApi/auth.js`) to keep the two in
 * parity. The developer app delegates *login* to accounts (redirect +
 * SessionSyncFrame), so it only needs the two calls that run inside its own
 * session: rotate the session (`/auth/refresh`, used by axiosInstance's 401
 * interceptor) and read the current identity (`/auth/me`).
 *
 * Both ride the httpOnly VCS_SESSION cookie, so they use a dedicated client with
 * `withCredentials` and do NOT go through axiosInstance — a 401 here must not
 * recurse into the refresh interceptor.
 */
import axios from "axios";
import { API_BASE } from "../../lib/apiBase";

const authClient = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

/**
 * POST /auth/refresh — rotates VCS_SESSION and returns a fresh id token.
 * Requires a live session cookie; a 401 (no server-side refresh token) rejects
 * and is treated as non-fatal by axiosInstance's interceptor.
 */
export const apiRefreshSession = async () => {
  const response = await authClient.post("/auth/refresh", {});
  return response?.data; // { id_token, expires_at, ... }
};

/**
 * GET /auth/me — the current user's claims plus derived `orgId`/`superuser`.
 * Session-cookie authed. Returns null on 401 so callers can fall back to the
 * locally held bearer token rather than treating it as a hard failure.
 */
export const apiGetMe = async () => {
  try {
    const response = await authClient.get("/auth/me");
    return response?.data;
  } catch (error) {
    if (error?.response?.status === 401) return null;
    throw error;
  }
};
